import type { DualStreamQueriesFirstPageQuery$data } from './__generated__/DualStreamQueriesFirstPageQuery.graphql.ts';

/** Number of items to fetch per API call (overfetch into a client-side buffer). */
export const FETCH_SIZE = 100;
/** Number of items to reveal per scroll page. */
export const PAGE_SIZE = 40;

/** The folder or content connection returned by the dual-stream first-page query. */
type StreamConnection =
	| DualStreamQueriesFirstPageQuery$data['folders']
	| DualStreamQueriesFirstPageQuery$data['content'];

/**
 * Selects the visible slice of the dual-stream children (folders first, then
 * content) for a given `visibleCount`.
 *
 * Content is only revealed once the folder stream is exhausted AND every folder
 * is already shown — matching the display rules in {@link useDualStreamPagination}.
 * Shared by the hook and the SSR preloader so both derive the exact same visible
 * set; a mismatch would leave the SSR-preloaded store snapshot unused (see
 * `preloadFolderData`).
 */
export const selectVisibleNodes = <T>(
	folderNodes: readonly T[],
	contentNodes: readonly T[],
	foldersExhausted: boolean,
	visibleCount: number,
): T[] => {
	const allFolderNodesShown = visibleCount >= folderNodes.length;
	const showContent = foldersExhausted && allFolderNodesShown;

	if (!showContent) {
		return folderNodes.slice(0, visibleCount);
	}
	const remainingSlots = visibleCount - folderNodes.length;
	const visibleContent = remainingSlots > 0 ? contentNodes.slice(0, remainingSlots) : [];
	return [...folderNodes, ...visibleContent];
};

/** Collects the ids of a stream's nodes, skipping any node without an id. */
const collectIds = (stream: StreamConnection): string[] => {
	const ids: string[] = [];
	for (const node of stream?.nodes ?? []) {
		if (node?.id) {
			ids.push(node.id);
		}
	}
	return ids;
};

/**
 * Computes the child ids the dual-stream folder view requests on its first
 * render (`visibleCount === PAGE_SIZE`). Used by the SSR preloader to warm
 * `FolderOverviewChildrenContentQuery` with the exact `ids` the component will
 * look up, so collaborators / last-edited data is present on the first render.
 *
 * Mirrors the hook's `visibleNodes` derivation: a stream is exhausted when it has
 * no nodes or the server reports no further pages (see `buildStreamState`).
 */
export const getInitialVisibleChildIds = (
	folders: StreamConnection,
	content: StreamConnection,
): string[] => {
	const folderIds = collectIds(folders);
	const contentIds = collectIds(content);
	const foldersExhausted = folderIds.length === 0 || !(folders?.pageInfo?.hasNextPage ?? false);
	return selectVisibleNodes(folderIds, contentIds, foldersExhausted, PAGE_SIZE);
};
